import { useCallback, useEffect, useRef, useState } from 'react';

import { errorMessage } from '../services/apiError';
import {
  BUSY_STATUSES,
  gmailInviteService,
  GmailInviteBatch,
  GmailInviteStatus,
  InviteSource,
  InviteTarget,
} from '../services/gmailInviteService';
import { forceConsent } from '../utils/recordingBlur';

const POLL_MS = 2000;
const TARGET_KEY = 'gmail_invite_target';

/** On the return path when Invite all was pressed in Refer & earn (``refer``) or in the offer after sign-in
 * (``offer``), so Google lands back in that window. */
export const FROM_REFER_PARAM = 'gmail_invites_from';

/** The window Invite all was pressed in, which Google's return reopens. */
export type InviteReturn = 'refer' | 'offer';

/** What Google's last answer left on the page, dropped before going to Google again. */
const RETURN_PARAMS = ['gmail_invites', 'gmail_invites_error', 'gmail_invites_reason', FROM_REFER_PARAM];

const currentPath = (from?: string): string => {
  const params = new URLSearchParams(window.location.search);

  RETURN_PARAMS.forEach((name) => params.delete(name));
  if (from) params.set(FROM_REFER_PARAM, from);
  const query = params.toString();

  return `${window.location.pathname}${query ? `?${query}` : ''}`;
};

/** The target chosen before going to Google, so it is still chosen when Google sends the user back. */
export const rememberTarget = (target: InviteTarget): void => {
  try {
    sessionStorage.setItem(TARGET_KEY, JSON.stringify(target));
  } catch {
    // Blocked storage: the user picks the target again after Google.
  }
};

export const recallTarget = (): InviteTarget | null => {
  try {
    const raw = sessionStorage.getItem(TARGET_KEY);

    return raw ? JSON.parse(raw) as InviteTarget : null;
  } catch {
    return null;
  }
};

let statusRequest: Promise<GmailInviteStatus> | null = null;

/** Whether this user may invite from Gmail; one request per page load, shared by every button. */
export const useGmailInviteAvailable = (enabled: boolean): boolean => {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    statusRequest = statusRequest ?? gmailInviteService.status();
    statusRequest.then((status) => { if (!cancelled) setAvailable(status.available); })
      .catch(() => { statusRequest = null; });

    return () => { cancelled = true; };
  }, [enabled]);

  return available;
};

/** One batch, refreshed every two seconds while it is still reading contacts or sending. */
const useBatchFollower = () => {
  const [batch, setBatch] = useState<GmailInviteBatch | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const follow = useCallback((batchId: string) => {
    const tick = async () => {
      try {
        const next = await gmailInviteService.batch(batchId);

        setBatch(next);
        if (BUSY_STATUSES.has(next.status)) timer.current = window.setTimeout(() => void tick(), POLL_MS);
      } catch (err) {
        setLoadError(errorMessage(err, 'We couldn’t load your invites. Close this window and try again.'));
      }
    };

    if (timer.current !== null) window.clearTimeout(timer.current);
    void tick();
  }, []);

  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  return { batch, follow, loadError };
};

const useInviteStatus = (active: boolean, follow: (batchId: string) => void) => {
  const [status, setStatus] = useState<GmailInviteStatus | null>(null);
  const [round, setRound] = useState(0);
  const reload = useCallback(() => setRound((value) => value + 1), []);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;

    void gmailInviteService.status().then((next) => {
      if (cancelled) return;
      setStatus(next);
      if (next.available && next.latest_batch) follow(next.latest_batch.id);
    }).catch(() => undefined);

    return () => { cancelled = true; };
  }, [active, follow, round]);

  return { reload, status };
};

/** Invite all at once on the Gmail access allowed at sign-in, while it is held: false means go to Google. */
const startAtOnce = async (daily: boolean, source?: InviteSource): Promise<boolean> => (
  !(await gmailInviteService.inviteAllNow(daily, source)).connect
);

type GoToGoogle = (returnTo: string, inviteAll: boolean, daily?: boolean) => Promise<void>;

/** Invite all: at once on the Gmail access sign-in allowed while it is held (``onStarted`` reads the new batch), else
 * through Google. */
const useInviteAllPress = (
  goToGoogle: GoToGoogle, setActionError: (error: string | null) => void, onStarted: () => void,
  source: InviteSource | undefined, from: InviteReturn,
) => {
  const [starting, setStarting] = useState(false);
  const pressInviteAll = useCallback(async (daily: boolean) => {
    setActionError(null);
    setStarting(true);
    const started = await startAtOnce(daily, source).catch((err) => {
      setActionError(errorMessage(err, 'Invite all could not start. Try again.'));

      return null;
    }).finally(() => setStarting(false));

    if (started === false) await goToGoogle(currentPath(from), true, daily);
    else if (started) onStarted();
  }, [goToGoogle, onStarted, setActionError, source, from]);
  const inviteAll = useCallback(() => pressInviteAll(false), [pressInviteAll]);
  const startDaily = useCallback(() => pressInviteAll(true), [pressInviteAll]);

  return { inviteAll, startDaily, starting };
};

/** To Google's consent page: to choose people (Invite people), or to invite everyone found (Refer & earn). */
const useGoogleConnect = (
  setActionError: (error: string | null) => void, onStarted: () => void, source: InviteSource | undefined,
  from: InviteReturn,
) => {
  const [connecting, setConnecting] = useState(false);

  const goToGoogle = useCallback(async (returnTo: string, inviteAll: boolean, daily = false) => {
    setActionError(null);
    setConnecting(true);
    try {
      window.location.assign(await gmailInviteService.connect(returnTo, forceConsent(), inviteAll, daily, source));
    } catch (err) {
      setConnecting(false);
      setActionError(errorMessage(err, 'Gmail could not be connected. Try again.'));
    }
  }, [setActionError, source]);

  const connect = useCallback(async (target: InviteTarget) => {
    rememberTarget(target);
    await goToGoogle(currentPath(), false);
  }, [goToGoogle]);

  const press = useInviteAllPress(goToGoogle, setActionError, onStarted, source, from);

  return { connect, connecting, ...press };
};

const useInviteActions = (
  batch: GmailInviteBatch | null, follow: (batchId: string) => void, onStarted: () => void,
  source: InviteSource | undefined, from: InviteReturn,
) => {
  const [actionError, setActionError] = useState<string | null>(null);
  const google = useGoogleConnect(setActionError, onStarted, source, from);

  const send = useCallback(async (emails: string[], target: InviteTarget) => {
    if (!batch) return;
    setActionError(null);
    try {
      const result = await gmailInviteService.send(batch.id, emails, target, currentPath());

      if ('reconnect_url' in result) window.location.assign(result.reconnect_url);
      else follow(batch.id);
    } catch (err) {
      setActionError(errorMessage(err, 'The invites could not be sent. Try again.'));
    }
  }, [batch, follow]);

  const discard = useCallback(async () => {
    if (!batch) return;
    try {
      await gmailInviteService.discard(batch.id);
      follow(batch.id);
    } catch (err) {
      setActionError(errorMessage(err, 'The list could not be discarded. Try again.'));
    }
  }, [batch, follow]);

  return { actionError, ...google, discard, send };
};

export interface UseGmailInviteResult {
  status: GmailInviteStatus | null;
  batch: GmailInviteBatch | null;
  error: string | null;
  connecting: boolean;
  /** Invite all is starting at once, on the Gmail access sign-in allowed (no trip to Google). */
  starting: boolean;
  connect: (target: InviteTarget) => Promise<void>;
  /** Refer & earn: everyone found (up to the day's limit) is invited to 8080.AI; through Google unless sign-in
   * already allowed it. */
  inviteAll: () => Promise<void>;
  /** Refer & earn: Invite all now and the next batch every day, on lasting access from sign-in or from Google. */
  startDaily: () => Promise<void>;
  send: (emails: string[], target: InviteTarget) => Promise<void>;
  discard: () => Promise<void>;
}

/** The invite window's state: availability, the latest batch and its progress, and what the user can do. */
/** ``source``: where the window was opened from, kept on the batches it starts for the admin analytics. ``from``: the
 * window Google's answer reopens after Invite all. */
export const useGmailInvite = (
  active: boolean, notice?: string | null, source?: InviteSource, from: InviteReturn = 'refer',
): UseGmailInviteResult => {
  const { batch, follow, loadError } = useBatchFollower();
  const { reload, status } = useInviteStatus(active, follow);
  const { actionError, ...actions } = useInviteActions(batch, follow, reload, source, from);

  return { status, batch, error: actionError ?? loadError ?? notice ?? null, ...actions };
};
